import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTabParam } from '../hooks/useTabParam.js';
import { balancedColumns } from '../lib/balancedColumns.js';
import { useEffect, useRef, useState } from 'react';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Pill, type PillTone } from '../components/PillGroup.js';
import { Icon } from '../components/Icon.js';
import { RelatedModulesGrid } from '../components/related/RelatedModules.js';
import { DesignTab, DesignHeaderActions } from '../components/projects/DesignTab.js';
import { useSiteConnection } from '../hooks/useSiteConnection.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { SectionHeaderAction } from '../components/ui/SectionHeaderAction.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Select } from '../components/ui/Select.js';
import { ProjectInfoEditModal, LANGUAGE_OPTIONS } from '../components/projects/ProjectInfoEditModal.js';
import { KVRow } from '../components/ui/KVRow.js';
import { useOpportunityByProject } from '../hooks/useFunnels.js';
import { KVGrid } from '../components/ui/KVGrid.js';
import { Tabs } from '../components/ui/Tabs.js';
import { StepChips } from '../components/ui/StepChips.js';
import { Button } from '../components/ui/Button.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { useProject, useProjectRevisions, useUpdateProject, type ContentPlan } from '../hooks/useProjects.js';
import { useLead } from '../hooks/useLeads.js';
import { useReports, type ReportRow } from '../hooks/useReports.js';
import { parseServerDate } from '../lib/date.js';
import { useToast } from '../components/ui/Toast.js';
import { ProjectContextSection, PlanKickoffCard } from '../components/projects/ProjectContextSection.js';
import { ContactOutreachTab } from '../components/contacts/ContactDetail.js';
import { SeccionNewsletter } from '../components/projects/SeccionNewsletter.js';
import { useContact } from '../hooks/useContacts.js';
import { ContentPlanSection } from '../components/projects/ContentPlanSection.js';
import { ProjectClaudePanel } from '../components/projects/ProjectClaudePanel.js';
import { usePageEditorState } from '../components/projects/page-editor/usePageEditorState.js';
import { PageEditorPanel } from '../components/projects/page-editor/PageEditorPanel.js';
import type { AssistantTab } from '../components/ui/AssistantShell.js';
import { ApproveProjectModal } from '../components/projects/ApproveProjectModal.js';

const STATUS_TONES: Record<string, PillTone> = {
  draft:            'neutral',
  briefed:          'blue',
  content_planning: 'cyan',
  in_design:        'cyan',
  client_review: 'amber',
  revisions:     'warn',
  approved:      'success',
  delivered:     'info',
  archived:      'muted',
};

type TabValue = 'info' | 'outreach' | 'related' | 'contentPlan' | 'design' | 'newsletter';

function parseContentPlan(raw: string | null | undefined): ContentPlan | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as ContentPlan; } catch { return null; }
}

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('es', { month: 'long', day: 'numeric', year: 'numeric' });
}

function ProjectReportRow({ report, onView }: { report: ReportRow; onView: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onView}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onView(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
        padding: 'var(--evo-space-3) var(--evo-space-4)',
        background: 'var(--evo-bg-2)',
        borderRadius: 'var(--evo-r-sm)',
        border: '1px solid var(--evo-stroke)',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {report.title}
        </div>
        <div style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', marginTop: '2px' }}>
          {t('projects.reports.createdAt')} {parseServerDate(report.createdAt).toLocaleDateString('es', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
      <Pill tone="neutral" variant="subtle" size="sm">
        {report.type.replace(/_/g, ' ')}
      </Pill>
      <Icon name="arrow-right" size={12} />
    </div>
  );
}

export function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const toast = useToast();
  const { data: project, isLoading } = useProject(slug);
  const { data: projectContact } = useContact(project?.contactId ?? 0);
  const { data: opportunityLink } = useOpportunityByProject(project?.id ?? null);
  const { data: originLead } = useLead(project?.leadId ?? null);
  // Sin sitio conectado la pestaña de Revisión no tiene qué mostrar ni dónde
  // publicar; el componente lo dice, pero el dato se resuelve acá.
  const siteConnection = useSiteConnection(project?.id ?? 0);
  const { data: reportsData, isLoading: reportsLoading } = useReports(
    project ? { projectId: project.id } : {},
  );
  // La pestaña vive en la URL: volver con el navegador cae donde estabas.
  const [activeTab, setActiveTab] = useTabParam(
    ['info', 'outreach', 'related', 'contentPlan', 'design', 'newsletter'] as const, 'info');
  const [designStep, setDesignStep] = useState<1 | 2 | 3>(1);
  // Con página ya cargada en el editor, el proyecto ABRE en el paso 2 — el
  // paso 1 (plantilla/conexión) es cosa del arranque. Una sola vez por
  // montaje: después manda el clic del usuario en las fichas.
  const stepInitDone = useRef(false);
  useEffect(() => {
    if (stepInitDone.current || !project) return;
    stepInitDone.current = true;
    if (project.currentVersion > 0) setDesignStep(2);
  }, [project]);
  const [plan, setPlan] = useState<ContentPlan | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const { data: revisionsData } = useProjectRevisions(project?.id);

  // El editor visual vive acá porque sus controles están en el panel de Evo
  // Brain (otro punto del árbol) y la vista previa está en esta página: es el
  // ancestro común de los dos.
  const [previewVersion, setPreviewVersion] = useState(0);
  // El panel del editor aparece solo donde tiene sentido: pestaña Diseño, con
  // página generada y en un estado donde la vista previa se está mostrando.
  const showEditorPanel =
    activeTab === 'design' &&
    !!project &&
    project.currentVersion > 0 &&
    ['in_design', 'revisions', 'client_review'].includes(project.status);

  // Qué pestaña del panel de Evo Brain está abierta. Vive acá y no adentro del
  // shell porque entrar en modo Editar tiene que abrir las herramientas.
  const [assistantTab, setAssistantTab] = useState<AssistantTab>('chat');

  const editor = usePageEditorState({
    projectId: project?.id ?? 0,
    isViewingOld: previewVersion > 0 && previewVersion < (project?.currentVersion ?? 0),
    previewKey: `${String(project?.id ?? 0)}:${String(previewVersion)}:${String(project?.currentVersion ?? 0)}`,
    // Aplicar comentarios crea una versión: la vista previa tiene que saltar a
    // ella sola, si no se queda mirando la anterior y parece que no pasó nada.
    onVersionChange: setPreviewVersion,
  });

  // Entrar en modo comentar/editar abre las herramientas del panel sin que
  // haya que descubrir la pestaña; volver a interactuar regresa al chat.
  useEffect(() => {
    setAssistantTab(editor.mode === 'view' ? 'chat' : 'tools');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.mode]);
  const nextRevisionNumber = (revisionsData?.length ?? 0) + 1;
  const updateProject = useUpdateProject();

  useEffect(() => {
    setPlan(parseContentPlan(project?.contentPlan));
  }, [project?.contentPlan]);

  useEffect(() => {
    const typePart = project
      ? t(`projects.type.${project.type}`, { defaultValue: project.type })
      : '';
    const subtypePart = project?.subtype
      ? ` · ${t(`projects.subtype.${project.subtype}`, { defaultValue: project.subtype })}`
      : '';
    const subtitle = typePart + subtypePart;

    setOverride({
      title: project?.name ?? t('projects.title'),
      ...(subtitle ? { subtitle } : {}),
      // Editar vive en la tarjeta Detalles del proyecto (Información) —
      // el lápiz del TopBar se retiró (2026-08-30).
    });
    return () => setOverride({});
  }, [setOverride, project, t]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <LoadingState fullHeight />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <EmptyState title={t('common.notFound', { defaultValue: 'No encontrado' })} />
      </div>
    );
  }

  const statusTone = STATUS_TONES[project.status] ?? 'neutral';
  const projectReports = reportsData?.reports ?? [];

  // V2: tres pestañas fijas del patrón detalle + las fases propias de
  // Proyectos (Brief / Content Plan / Diseño). Reportes y cotizaciones
  // viven como tarjetas dentro de Relacionados.
  // El Brief se eliminó (2026-08-29): su lugar lo toma el CONTEXTO dentro de
  // Información. Ninguna pestaña condiciona a otra — todas libres siempre.
  const tabs = [
    { value: 'info' as const,    label: t('detail.tabs.info') },
    { value: 'outreach' as const, label: t('detail.tabs.outreach') },
    { value: 'related' as const, label: t('detail.tabs.related') },
    { value: 'contentPlan' as const, label: t('projects.tabs.contentPlan') },
    { value: 'design' as const, label: t('projects.tabs.design') },
    { value: 'newsletter' as const, label: t('newsletter.title', { defaultValue: 'Newsletter' }) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* SubNav + Tabs — merged single row */}
      <div style={{
        height: 'var(--evo-subnav-h)',
        borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0,
        background: 'var(--evo-bg)',
      }}>
        <div style={{ padding: '0 var(--evo-page-pad-x)', height: '100%' }}>
        <div style={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>

        {/* Left: back + separator + status pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)', flexShrink: 0, paddingRight: 'var(--evo-space-4)' }}>
          <Button
            variant="link"
            tone="muted"
            size="sm"
            icon={<Icon name="chevron-left" size={14} />}
            onClick={() => navigate('/proyectos')}
          >
            {t('projects.detail.back')}
          </Button>
          <div style={{ width: '1px', height: 'var(--evo-divider-h)', background: 'var(--evo-stroke)', flexShrink: 0 }} />
          <Pill tone={statusTone} variant="outline" size="sm">
            {t(`projects.status.${project.status}`, { defaultValue: project.status })}
          </Pill>
        </div>

        {/* Tabs: fill remaining space, centered horizontally */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
          <Tabs<TabValue>
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            noBorder
          />
          {/* Fichas de paso — junto a Diseño, solo con esa pestaña activa.
              La activa se rellena en cian; los pasos son LIBRES (el estado
              del proyecto informa, no bloquea). */}
          {activeTab === 'design' && (
            <StepChips
              count={3}
              value={designStep}
              onChange={n => setDesignStep(n as 1 | 2 | 3)}
              titleFor={n => t(`projects.design.tab.steps.${n === 1 ? 'one' : n === 2 ? 'two' : 'three'}`)}
            />
          )}
        </div>

        {/* Derecha: Ver · Editar de la página.
            Acá estaba la navegación entre proyectos (Anterior · 1 de 2 ·
            Siguiente), que nadie usa: para ir a otro proyecto se vuelve a la
            lista. El selector de modo, en cambio, se toca todo el tiempo
            mientras se trabaja en la pestaña Diseño, y estaba enterrado en el
            panel de la derecha. */}
        {/* El selector de modo se movió a la banda del paso 2 (DesignTab);
            el pill de cambios pendientes se queda como recordatorio visible. */}
        {(activeTab === 'design' || (showEditorPanel && editor.pendingCount > 0)) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexShrink: 0, paddingLeft: 'var(--evo-content-pad-x)' }}>
            {showEditorPanel && editor.pendingCount > 0 && (
              <Pill variant="outline" tone="cyan" size="sm">
                {t('projects.editor.pendingChanges', { count: editor.pendingCount })}
              </Pill>
            )}
            {/* Enviar al cliente · Aprobado · Publicar — justificados a la
                derecha de la fila de pestañas; las pestañas quedan centradas. */}
            {activeTab === 'design' && (
              <DesignHeaderActions project={project} />
            )}
          </div>
        )}

        </div>
        </div>
      </div>

      {/* Main row: left (content) + right (Claude panel) */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: scrollable content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>

            {/* ── Tab: Información ──
                Sin doble contenedor: las dos tarjetas van directo sobre la
                página (duo a altura pareja), igual que los otros módulos.
                Editar vive arriba en el TopBar (lápiz). */}
            {activeTab === 'info' && (
              <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
              <div style={{ paddingTop: 'var(--evo-space-6)' }}>
                  <div className="evo-duo-grid">

                    {/* Left — Project Details */}
                    <DetailSection
                      title={t('projects.detail.cardDetails')}
                      divider={false}
                      titleRight={
                        <SectionHeaderAction onClick={() => setEditInfoOpen(true)}>
                          {t('common.edit')}
                        </SectionHeaderAction>
                      }
                    >
                      <KVGrid columns={1}>
                        <KVRow label={t('projects.columns.type')} value={t(`projects.type.${project.type}`, { defaultValue: project.type })} />
                        <KVRow label={t('projects.columns.subtype')} value={project.subtype ? t(`projects.subtype.${project.subtype}`, { defaultValue: project.subtype }) : '—'} />
                        <KVRow
                          label={t('projects.fields.language')}
                          value={project.language}
                          renderValue={() => (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                              <Select
                                value={project.language}
                                onChange={v => { if (v) updateProject.mutate({ id: project.id, language: v }); }}
                                options={LANGUAGE_OPTIONS}
                              />
                              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
                                {t('projects.fields.languageHint')}
                              </span>
                            </div>
                          )}
                        />
                        <KVRow label={t('projects.columns.deadline')} value={project.desiredDeadline ? new Date(project.desiredDeadline).toLocaleDateString('es', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'} last />
                      </KVGrid>
                    </DetailSection>

                    {/* Right — Origin */}
                    <DetailSection title={t('projects.detail.cardOrigin')} divider={false}>
                      <KVGrid columns={1}>
                        <KVRow label={t('projects.columns.contact')} value={project.contactDisplayName ?? String(project.contactId)} />
                        <KVRow label={t('projects.columns.slug')} value={project.slug} />
                        <KVRow label={t('projects.columns.created')} value={fmtDate(project.createdAt)} {...(!project.leadId ? { last: true } : {})} />
                        {project.leadId && (
                          <KVRow
                            label={t('projects.detail.leadOrigin')}
                            value={originLead?.businessName ?? project.leadId}
                            renderValue={() => (
                              <Button
                                variant="ghost"
                                tone="cyan"
                                size="sm"
                                onClick={() => navigate(`/leads/${project.leadId}`)}
                              >
                                {originLead?.businessName ?? project.leadId} →
                              </Button>
                            )}
                            last={!opportunityLink?.opportunity}
                          />
                        )}
                        {/* Enlace de vuelta al tablero: el proyecto que salió
                            de una oportunidad tiene que poder volver a ella. */}
                        {opportunityLink?.opportunity && (
                          <KVRow
                            label={t('opportunities.backlink')}
                            value={opportunityLink.opportunity.title}
                            renderValue={() => (
                              <Button
                                variant="ghost"
                                tone="cyan"
                                size="sm"
                                onClick={() => navigate(`/embudos?funnel=${opportunityLink.opportunity!.funnelId}`)}
                              >
                                {opportunityLink.opportunity!.title} →
                              </Button>
                            )}
                            last
                          />
                        )}
                      </KVGrid>
                    </DetailSection>

                  </div>

                  {/* Fila 2: el contacto del proyecto — sus datos viven acá,
                      no en una pestaña aparte. */}
                  <div className="evo-duo-grid" style={{ marginTop: 'var(--evo-related-gap)' }}>
                    <DetailSection
                      title={t('projects.detail.cardContact')}
                      divider={false}
                      titleRight={
                        <SectionHeaderAction onClick={() => navigate(`/contacts/${project.contactId}`)}>
                          {t('projects.detail.openContact')}
                        </SectionHeaderAction>
                      }
                    >
                      <KVGrid columns={1}>
                        <KVRow label={t('contacts.name')} value={[projectContact?.firstName, projectContact?.lastName].filter(Boolean).join(' ') || (project.contactDisplayName ?? '—')} />
                        <KVRow label={t('contacts.businessName')} value={projectContact?.businessName ?? undefined} />
                        <KVRow label={t('contacts.email')} value={projectContact?.primaryEmail ?? undefined} />
                        <KVRow label={t('contacts.phone')} value={projectContact?.primaryPhone ?? undefined} />
                        <KVRow label={t('contacts.website')} value={projectContact?.website ?? undefined} last />
                      </KVGrid>
                    </DetailSection>
                    <PlanKickoffCard project={project} onCreated={() => setActiveTab('contentPlan')} />
                  </div>

                  {/* Fila 3: contexto para EvoBrain — reemplaza al Brief. */}
                  <div style={{ marginTop: 'var(--evo-related-gap)' }}>
                    <ProjectContextSection project={project} />
                  </div>
              </div>
              </div>
            )}

            {/* ── Tab: Plan de contenido ── */}
            {activeTab === 'contentPlan' && (
              <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
              <div style={{ paddingTop: 'var(--evo-space-6)' }}>
                <ContentPlanSection
                  project={project}
                  plan={plan}
                  onPlanChange={setPlan}
                  divider={false}
                  onDesignStarted={() => setActiveTab('design')}
                  onReportGenerated={() => setActiveTab('related')}
                  onSendContentPlan={() => setActiveTab('outreach')}
                />
              </div>
              </div>
            )}

            {/* ── Tab: Relacionados — rejilla compartida de 5 módulos, la
                misma estructura que Contactos y Prospectos. Reportes se
                override con la lista scopeada al proyecto; el resto consulta
                por el contacto del proyecto. */}
            {activeTab === 'related' && (
              <div style={{ padding: 'var(--evo-space-6) var(--evo-page-pad-x)' }}>
                <RelatedModulesGrid
                  contactId={project.contactId}
                  contactName={project.contactDisplayName ?? ''}
                  overrides={{
                    reports: {
                      content: reportsLoading ? (
                        <div style={{ padding: 'var(--evo-space-4) 0' }}>
                          <LoadingState size="sm" />
                        </div>
                      ) : projectReports.length === 0 ? (
                        <EmptyState title={t('projects.reports.empty')} />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                          {projectReports.map(r => (
                            <ProjectReportRow
                              key={r.id}
                              report={r}
                              onView={() => navigate(`/reports/${r.slug}`)}
                            />
                          ))}
                        </div>
                      ),
                    },
                  }}
                />
              </div>
            )}

            {/* ── Tab: Diseño ── */}
            {/* Diseño: TRES PASOS libres (DesignTab) — sin bloqueo por estado. */}
            {activeTab === 'design' && (
              <DesignTab
                project={project}
                editor={editor}
                step={designStep}
                onStepChange={setDesignStep}
                onVersionChange={setPreviewVersion}
                onOpenApprove={() => setIsApproveModalOpen(true)}
              />
            )}

            {/* Approve modal — a este nivel para escapar la columna flex */}
            {isApproveModalOpen && (
              <ApproveProjectModal
                projectId={project.id}
                revisionNumber={nextRevisionNumber}
                currentVersion={project.currentVersion}
                onClose={() => setIsApproveModalOpen(false)}
                onApproved={() => setIsApproveModalOpen(false)}
              />
            )}

            {/* ── Tab: Outreach ── */}
            {/* El outreach es DEL CONTACTO: el historial completo (emails,
                DMs, hitos de proyectos y facturación) vive en su timeline —
                los envíos de este proyecto aparecen ahí como eventos. */}
            {activeTab === 'outreach' && (
              <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
                <ContactOutreachTab contactId={project.contactId} />
              </div>
            )}

            {activeTab === 'newsletter' && (
              <div style={{ padding: '0 var(--evo-page-pad-x)' }}>
                <SeccionNewsletter projectId={project.id} />
              </div>
            )}


          </div>

        </div>

        {/* Right: Claude panel */}
        <ProjectClaudePanel
          project={project}
          plan={plan}
          onPlanChange={setPlan}
          section={activeTab}
          {...(showEditorPanel
            ? {
                toolsContent: <PageEditorPanel editor={editor} />,
                activeTab: assistantTab,
                onActiveTabChange: setAssistantTab,
              }
            : {})}
        />

      </div>

      {editInfoOpen && (
        <ProjectInfoEditModal
          project={project}
          open={editInfoOpen}
          onClose={() => setEditInfoOpen(false)}
        />
      )}
    </div>
  );
}
